import { ChevronLeft, ChevronRight, Clock, MapPinOff, Pencil, Play, Square } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { Action, GeoPoint, Resource, Role } from '@buildbee/contracts';
import { can } from '@buildbee/domain';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Field,
  FormError,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  formatDate,
  formatMoney,
  useToast,
  type SelectOption,
} from '@buildbee/ui';
import { errorCode, errorMessage, trpc, type RouterOutputs } from '../../lib/trpc';
import { StatusBadge } from './components';
import { centsToDollars, dollarsToCents, todayLocal } from './lib';

/**
 * Time clock building blocks shared by the Timesheets page, the project Time tab and the
 * clock widget on the projects list.
 */

export type TimeEntry = RouterOutputs['projects']['timeclock']['timesheet']['entries'][number];
export type TimesheetData = RouterOutputs['projects']['timeclock']['timesheet'];
export type TimeMember = RouterOutputs['projects']['timeclock']['members'][number];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Minutes as decimal hours ("8.50"). */
export function hours(minutes: number | null | undefined): string {
  return ((minutes ?? 0) / 60).toFixed(2);
}

/** Running clock as h:mm:ss. */
export function elapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Calendar date arithmetic on `YYYY-MM-DD` (UTC so DST never shifts the day). */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Date -> value for `<input type="datetime-local">` in local time. */
export function toLocalInput(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function formatTime(value: Date | string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Browser position for the geofence check; null when unavailable or denied (never blocks). */
export function currentPosition(timeoutMs = 6000): Promise<GeoPoint | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs + 500);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(timer);
        resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracyM: Math.min(100_000, Math.round(p.coords.accuracy)) });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}

/** Save text as a file in the browser. */
export function downloadText(filename: string, text: string, type = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Ticks every second while `active`. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

/**
 * Who the viewer is and what time controls to show. UI hint only; the API enforces.
 * Managers = daily_log:approve (PMs, admins, owners).
 */
export function useTimeAccess() {
  const current = trpc.workspace.current.useQuery(undefined, { staleTime: 5 * 60_000 });
  const mine = trpc.workspace.roles.mine.useQuery(undefined, { staleTime: 5 * 60_000 });
  const me = trpc.workspace.me.get.useQuery(undefined, { staleTime: 5 * 60_000 });
  const role = (current.data?.membership.role ?? 'field') as Role;
  const cells = mine.data?.cells;
  return useMemo(() => {
    const builtIn = (action: Action, resource: Resource) => can({ userId: '', role }, action, resource);
    // The company's role ticks once loaded; the built-in defaults until then.
    const has = (action: Action, resource: Resource) => role === 'owner' || (cells ? (cells as readonly string[]).includes(`${resource}:${action}`) : builtIn(action, resource));
    const manager = has('approve', 'daily_log');
    return {
      loading: current.isLoading || me.isLoading,
      userId: me.data?.user.id,
      role,
      manager,
      canClock: has('create', 'daily_log'),
      canExport: manager,
      canSetRates: has('update', 'member'),
      canViewIntegrations: has('view', 'integration'),
      canPushAccounting: has('update', 'integration'),
    };
  }, [current.isLoading, me.isLoading, me.data, role, cells]);
}

/** Active projects for pickers (the ones the viewer can see). */
function useProjectOptions(enabled: boolean): SelectOption[] {
  const list = trpc.projects.list.useQuery({ limit: 100, statuses: ['preconstruction', 'active', 'punch', 'on_hold'] }, { enabled, staleTime: 60_000 });
  return useMemo(() => (list.data?.items ?? []).map((p) => ({ value: p.id, label: `${p.number} ${p.name}` })), [list.data]);
}

/** Library cost codes as suggestions (silently empty for roles that cannot see budgets). */
function CostCodeSuggestions({ id, enabled }: { id: string; enabled: boolean }) {
  const codes = trpc.financials.costCodes.list.useQuery({ includeDivisions: false }, { enabled, retry: false, staleTime: 10 * 60_000 });
  return (
    <datalist id={id}>
      {(codes.data ?? []).map((c) => (
        <option key={c.code} value={c.code}>
          {c.name}
        </option>
      ))}
    </datalist>
  );
}

/** Invalidate every time clock query (timesheets, current shift, labour cost). */
function useInvalidateTime() {
  const utils = trpc.useUtils();
  return () => utils.projects.timeclock.invalidate();
}

// ---------------------------------------------------------------------------
// Week picker
// ---------------------------------------------------------------------------

export function WeekPicker({ weekOf, onChange, week }: { weekOf: string; onChange: (d: string) => void; week?: { start: string; end: string } }) {
  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="icon" aria-label="Previous week" onClick={() => onChange(addDays(weekOf, -7))}>
        <ChevronLeft aria-hidden />
      </Button>
      <span className="figures min-w-[13rem] px-2 text-center text-sm font-medium">{week ? `${formatDate(week.start)} – ${formatDate(week.end)}` : formatDate(weekOf)}</span>
      <Button variant="outline" size="icon" aria-label="Next week" onClick={() => onChange(addDays(weekOf, 7))}>
        <ChevronRight aria-hidden />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onChange(todayLocal())}>
        This week
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clock in / out widget
// ---------------------------------------------------------------------------

function ClockInDialog({ open, onOpenChange, defaultProjectId }: { open: boolean; onOpenChange: (o: boolean) => void; defaultProjectId?: string }) {
  const toast = useToast();
  const invalidate = useInvalidateTime();
  const projects = useProjectOptions(open);
  const [v, setV] = useState({ projectId: defaultProjectId ?? '', costCode: '', notes: '' });
  const [locating, setLocating] = useState(false);
  const clockIn = trpc.projects.timeclock.clockIn.useMutation();
  const submit = async () => {
    setLocating(true);
    const geo = await currentPosition();
    setLocating(false);
    try {
      const r = await clockIn.mutateAsync({ projectId: v.projectId || null, costCode: v.costCode.trim() || null, notes: v.notes.trim() || null, geo, source: 'web' });
      if (r.clockInOffsite) {
        const distance = 'distanceM' in r && typeof r.distanceM === 'number' ? ` You are about ${Math.round(r.distanceM)} m from the site.` : '';
        toast.toast({ title: 'Clocked in off site', description: `Your location is outside the job site geofence.${distance}`, variant: 'warning' });
      } else toast.success('Clocked in', r.projectName ?? 'Shop / office time');
      await invalidate();
      setV({ projectId: defaultProjectId ?? '', costCode: '', notes: '' });
      onOpenChange(false);
    } catch {
      // shown below
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Clock in" description="Your location is checked against the job site when your browser shares it.">
        <div className="space-y-4">
          <Field label="Project" htmlFor="ci-project">
            <Select id="ci-project" value={v.projectId} onChange={(e) => setV({ ...v, projectId: e.target.value })} options={[{ value: '', label: 'No project (shop / office)' }, ...projects]} />
          </Field>
          <Field label="Cost code" htmlFor="ci-code">
            <Input id="ci-code" list="ci-code-list" placeholder="e.g. 06 10 00" value={v.costCode} onChange={(e) => setV({ ...v, costCode: e.target.value })} />
            <CostCodeSuggestions id="ci-code-list" enabled={open} />
          </Field>
          <Field label="Notes" htmlFor="ci-notes">
            <Textarea id="ci-notes" rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} />
          </Field>
          <FormError message={clockIn.error ? errorMessage(clockIn.error) : null} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={locating || clockIn.isPending}>
              <Play aria-hidden /> Clock in
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClockOutDialog({ open, onOpenChange, startedAt }: { open: boolean; onOpenChange: (o: boolean) => void; startedAt: Date }) {
  const toast = useToast();
  const invalidate = useInvalidateTime();
  const [breakMinutes, setBreakMinutes] = useState('0');
  const [notes, setNotes] = useState('');
  const [locating, setLocating] = useState(false);
  const clockOut = trpc.projects.timeclock.clockOut.useMutation();
  const brk = Number(breakMinutes);
  const validBreak = Number.isInteger(brk) && brk >= 0 && brk <= 720;
  const submit = async () => {
    setLocating(true);
    const geo = await currentPosition();
    setLocating(false);
    try {
      const r = await clockOut.mutateAsync({ breakMinutes: brk, notes: notes.trim() || null, geo });
      toast.success('Clocked out', `${hours(r.durationMinutes)} hours recorded`);
      await invalidate();
      setBreakMinutes('0');
      setNotes('');
      onOpenChange(false);
    } catch {
      // shown below
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Clock out" description={`On the clock since ${formatTime(startedAt)}.`}>
        <div className="space-y-4">
          <Field label="Break (minutes)" htmlFor="co-break" error={validBreak ? undefined : 'Enter whole minutes, up to 720'}>
            <Input id="co-break" type="number" min={0} max={720} step={5} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} />
          </Field>
          <Field label="Notes" htmlFor="co-notes">
            <Textarea id="co-notes" rows={2} placeholder="What did you work on?" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <FormError message={clockOut.error ? errorMessage(clockOut.error) : null} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={locating || clockOut.isPending} disabled={!validBreak}>
              <Square aria-hidden /> Clock out
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Clock in / out for office users. `compact` renders a single header button; otherwise a card
 * with the running shift.
 */
export function ClockWidget({ compact, defaultProjectId }: { compact?: boolean; defaultProjectId?: string }) {
  const current = trpc.projects.timeclock.current.useQuery(undefined, { refetchInterval: 5 * 60_000 });
  const [dialog, setDialog] = useState<'in' | 'out' | null>(null);
  const shift = current.data;
  const startedAt = shift ? new Date(shift.clockInAt) : null;
  const now = useNow(!!shift);
  // A workspace where the viewer cannot record time: show nothing rather than a broken control.
  if (current.error && errorCode(current.error) === 'FORBIDDEN') return null;
  const dialogs = (
    <>
      <ClockInDialog open={dialog === 'in'} onOpenChange={(o) => setDialog(o ? 'in' : null)} defaultProjectId={defaultProjectId} />
      {startedAt ? <ClockOutDialog open={dialog === 'out'} onOpenChange={(o) => setDialog(o ? 'out' : null)} startedAt={startedAt} /> : null}
    </>
  );
  if (compact) {
    return (
      <>
        {shift && startedAt ? (
          <Button variant="outline" onClick={() => setDialog('out')} title={shift.projectName ?? 'Shop / office time'}>
            <span className="size-2 rounded-full bg-success" aria-hidden />
            <span className="figures">{elapsed(now - startedAt.getTime())}</span>
            <span className="text-muted-foreground">Clock out</span>
          </Button>
        ) : (
          <Button variant="outline" onClick={() => setDialog('in')} loading={current.isLoading} disabled={!!current.error}>
            <Clock aria-hidden /> Clock in
          </Button>
        )}
        {dialogs}
      </>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-card px-5 py-4 shadow-card">
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-muted-foreground">Time clock</p>
        {current.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : current.error ? (
          <p className="text-sm text-destructive">{errorMessage(current.error)}</p>
        ) : shift && startedAt ? (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="figures text-[22px] font-medium leading-tight">{elapsed(now - startedAt.getTime())}</span>
            <span className="truncate text-sm text-muted-foreground">
              {shift.projectName ? `${shift.projectNumber ?? ''} ${shift.projectName}`.trim() : 'Shop / office time'}
              {shift.costCode ? ` · ${shift.costCode}` : ''} · since {formatTime(startedAt)}
            </span>
            {shift.clockInOffsite ? (
              <Badge variant="warning">
                <MapPinOff className="size-3" aria-hidden /> Off site
              </Badge>
            ) : null}
          </div>
        ) : (
          <p className="text-sm">You are not clocked in.</p>
        )}
      </div>
      {shift ? (
        <Button onClick={() => setDialog('out')}>
          <Square aria-hidden /> Clock out
        </Button>
      ) : (
        <Button onClick={() => setDialog('in')} disabled={current.isLoading || !!current.error}>
          <Play aria-hidden /> Clock in
        </Button>
      )}
      {dialogs}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual entry / edit
// ---------------------------------------------------------------------------

export function TimeEntryDialog({
  open,
  onOpenChange,
  entry,
  members,
  defaultUserId,
  defaultProjectId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Edit this entry; omit to add a manual entry. */
  entry?: TimeEntry;
  /** Managers pick the worker; omit for the viewer's own time. */
  members?: TimeMember[];
  defaultUserId?: string;
  defaultProjectId?: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={entry ? 'Edit time entry' : 'Add time entry'} description={entry ? `${entry.userName ?? 'Worker'} · ${formatDate(entry.workDate)}` : 'For a missed clock-in or office time.'} size="lg">
        {open ? <TimeEntryForm entry={entry} members={members} defaultUserId={defaultUserId} defaultProjectId={defaultProjectId} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function TimeEntryForm({ entry, members, defaultUserId, defaultProjectId, onDone }: { entry?: TimeEntry; members?: TimeMember[]; defaultUserId?: string; defaultProjectId?: string; onDone: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateTime();
  const projects = useProjectOptions(true);
  const running = !!entry && !entry.clockOutAt;
  const [v, setV] = useState(() => {
    const start = new Date();
    start.setHours(7, 0, 0, 0);
    const end = new Date(start);
    end.setHours(15, 30, 0, 0);
    return {
      userId: entry?.userId ?? defaultUserId ?? '',
      projectId: entry?.projectId ?? defaultProjectId ?? '',
      costCode: entry?.costCode ?? '',
      clockIn: toLocalInput(entry?.clockInAt ?? start),
      clockOut: entry ? toLocalInput(entry.clockOutAt) : toLocalInput(end),
      breakMinutes: String(entry?.breakMinutes ?? 30),
      notes: entry?.notes ?? '',
    };
  });
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((s) => ({ ...s, [k]: e.target.value }));
  const create = trpc.projects.timeclock.create.useMutation();
  const update = trpc.projects.timeclock.update.useMutation();
  const brk = Number(v.breakMinutes);
  const inAt = v.clockIn ? new Date(v.clockIn) : null;
  const outAt = v.clockOut ? new Date(v.clockOut) : null;
  const problem = !inAt || Number.isNaN(inAt.getTime())
    ? 'Enter the clock-in time'
    : !running && (!outAt || Number.isNaN(outAt.getTime()))
      ? 'Enter the clock-out time'
      : !running && outAt && outAt <= inAt
        ? 'Clock-out must be after clock-in'
        : !Number.isInteger(brk) || brk < 0 || brk > 720
          ? 'Break must be whole minutes, up to 720'
          : null;
  const save = async () => {
    if (problem || !inAt) return;
    const common = { projectId: v.projectId || null, costCode: v.costCode.trim() || null, notes: v.notes.trim() || null };
    try {
      if (entry) {
        await update.mutateAsync({ id: entry.id, expectedVersion: entry.version, ...common, clockInAt: inAt, ...(running ? {} : { clockOutAt: outAt!, breakMinutes: brk }) });
        toast.success('Time entry updated');
      } else {
        await create.mutateAsync({ ...common, userId: v.userId || undefined, clockInAt: inAt, clockOutAt: outAt!, breakMinutes: brk });
        toast.success('Time entry added');
      }
      await invalidate();
      onDone();
    } catch {
      // shown below
    }
  };
  const err = create.error ?? update.error;
  const showWorker = !entry && members && members.length > 0;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {showWorker ? (
          <Field label="Worker" htmlFor="te-user" className="sm:col-span-2" required>
            <Select id="te-user" value={v.userId} onChange={set('userId')} options={members.map((m) => ({ value: m.userId, label: m.name }))} placeholder="Select a worker" />
          </Field>
        ) : null}
        <Field label="Project" htmlFor="te-project">
          <Select id="te-project" value={v.projectId} onChange={set('projectId')} options={[{ value: '', label: 'No project (shop / office)' }, ...projects, ...(entry?.projectId && !projects.some((p) => p.value === entry.projectId) ? [{ value: entry.projectId, label: `${entry.projectNumber ?? ''} ${entry.projectName ?? 'Project'}`.trim() }] : [])]} />
        </Field>
        <Field label="Cost code" htmlFor="te-code">
          <Input id="te-code" list="te-code-list" value={v.costCode} onChange={set('costCode')} />
          <CostCodeSuggestions id="te-code-list" enabled />
        </Field>
        <Field label="Clock in" htmlFor="te-in" required>
          <Input id="te-in" type="datetime-local" value={v.clockIn} onChange={set('clockIn')} />
        </Field>
        <Field label="Clock out" htmlFor="te-out" required={!running} description={running ? 'Still on the clock: use Clock out to end this shift.' : undefined}>
          <Input id="te-out" type="datetime-local" value={v.clockOut} onChange={set('clockOut')} disabled={running} />
        </Field>
        <Field label="Break (minutes)" htmlFor="te-break">
          <Input id="te-break" type="number" min={0} max={720} step={5} value={v.breakMinutes} onChange={set('breakMinutes')} disabled={running} />
        </Field>
      </div>
      <Field label="Notes" htmlFor="te-notes">
        <Textarea id="te-notes" rows={2} value={v.notes} onChange={set('notes')} />
      </Field>
      <FormError message={err ? errorMessage(err) : null} />
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={() => void save()} loading={create.isPending || update.isPending} disabled={!!problem || (showWorker && !v.userId)} title={problem ?? undefined}>
          {entry ? 'Save' : 'Add entry'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review: entries table with bulk submit / approve / reject
// ---------------------------------------------------------------------------

function RejectDialog({ open, onOpenChange, count, onConfirm, loading }: { open: boolean; onOpenChange: (o: boolean) => void; count: number; onConfirm: (reason: string) => void; loading: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={`Reject ${count} ${count === 1 ? 'entry' : 'entries'}`} description="The worker sees the reason and can correct and resubmit." size="sm">
        <div className="space-y-4">
          <Field label="Reason" htmlFor="rj-reason" required>
            <Textarea id="rj-reason" rows={3} autoFocus value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={!reason.trim()} loading={loading} onClick={() => onConfirm(reason.trim())}>
              Reject
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A week's entries with selection and the review actions the viewer may use. Workers submit their
 * own closed entries; managers approve or reject (the API stops a manager approving their own time
 * unless they are an owner or admin).
 */
export function TimeEntriesTable({
  entries,
  viewerId,
  manager,
  showWorker = true,
  showProject = true,
  onEdit,
  emptyText = 'No time recorded this week.',
}: {
  entries: TimeEntry[];
  viewerId: string | undefined;
  manager: boolean;
  showWorker?: boolean;
  showProject?: boolean;
  onEdit?: (e: TimeEntry) => void;
  emptyText?: string;
}) {
  const toast = useToast();
  const invalidate = useInvalidateTime();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejecting, setRejecting] = useState(false);
  const sorted = useMemo(() => [...entries].sort((a, b) => (a.userName ?? '').localeCompare(b.userName ?? '') || new Date(a.clockInAt).getTime() - new Date(b.clockInAt).getTime()), [entries]);
  // Drop selections that are no longer on screen (week or filter changed).
  useEffect(() => {
    setSelected((s) => {
      const ids = new Set(entries.map((e) => e.id));
      const next = new Set([...s].filter((id) => ids.has(id)));
      return next.size === s.size ? s : next;
    });
  }, [entries]);
  const picked = sorted.filter((e) => selected.has(e.id));
  const toSubmit = picked.filter((e) => e.userId === viewerId && e.clockOutAt && (e.status === 'open' || e.status === 'rejected'));
  const toApprove = manager ? picked.filter((e) => e.clockOutAt && e.status !== 'approved') : [];
  const toReject = manager ? picked.filter((e) => e.clockOutAt && e.status !== 'rejected') : [];
  const done = async (title: string) => {
    toast.success(title);
    setSelected(new Set());
    await invalidate();
  };
  const onError = (e: unknown) => toast.error('Could not update time', errorMessage(e));
  const submit = trpc.projects.timeclock.submit.useMutation({ onSuccess: (r) => done(`${r.length} ${r.length === 1 ? 'entry' : 'entries'} submitted`), onError });
  const approve = trpc.projects.timeclock.approve.useMutation({ onSuccess: (r) => done(`${r.length} ${r.length === 1 ? 'entry' : 'entries'} approved`), onError });
  const reject = trpc.projects.timeclock.reject.useMutation({
    onSuccess: async (r) => {
      setRejecting(false);
      await done(`${r.length} ${r.length === 1 ? 'entry' : 'entries'} rejected`);
    },
    onError,
  });
  const allIds = sorted.map((e) => e.id);
  const allChecked = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggle = (id: string, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  if (!sorted.length) return <p className="px-1 py-6 text-center text-sm text-muted-foreground">{emptyText}</p>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">{picked.length ? `${picked.length} selected` : 'Select entries to review'}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          {sorted.some((e) => e.userId === viewerId) ? (
            <Button size="sm" variant="outline" disabled={!toSubmit.length} loading={submit.isPending} onClick={() => submit.mutate({ ids: toSubmit.map((e) => e.id) })}>
              Submit{toSubmit.length ? ` (${toSubmit.length})` : ''}
            </Button>
          ) : null}
          {manager ? (
            <>
              <Button size="sm" variant="outline" disabled={!toReject.length} onClick={() => setRejecting(true)}>
                Reject{toReject.length ? ` (${toReject.length})` : ''}
              </Button>
              <Button size="sm" disabled={!toApprove.length} loading={approve.isPending} onClick={() => approve.mutate({ ids: toApprove.map((e) => e.id) })}>
                Approve{toApprove.length ? ` (${toApprove.length})` : ''}
              </Button>
            </>
          ) : null}
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox aria-label="Select all entries" checked={allChecked} onCheckedChange={(c) => setSelected(c === true ? new Set(allIds) : new Set())} />
            </TableHead>
            <TableHead>Date</TableHead>
            {showWorker ? <TableHead>Worker</TableHead> : null}
            {showProject ? <TableHead>Project</TableHead> : null}
            <TableHead>Cost code</TableHead>
            <TableHead>In</TableHead>
            <TableHead>Out</TableHead>
            <TableHead className="text-right">Break</TableHead>
            <TableHead className="text-right">Hours</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Notes</TableHead>
            {onEdit ? <TableHead className="w-10" /> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((e) => (
            <TableRow key={e.id} data-state={selected.has(e.id) ? 'selected' : undefined}>
              <TableCell>
                <Checkbox aria-label={`Select entry ${formatDate(e.workDate)} ${e.userName ?? ''}`.trim()} checked={selected.has(e.id)} onCheckedChange={(c) => toggle(e.id, c === true)} />
              </TableCell>
              <TableCell className="figures whitespace-nowrap">{formatDate(e.workDate, undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</TableCell>
              {showWorker ? <TableCell className="whitespace-nowrap font-medium">{e.userName ?? 'Former member'}</TableCell> : null}
              {showProject ? <TableCell className="max-w-[14rem] truncate">{e.projectName ? `${e.projectNumber ?? ''} ${e.projectName}`.trim() : <span className="text-muted-foreground">Shop / office</span>}</TableCell> : null}
              <TableCell className="figures">{e.costCode ?? '—'}</TableCell>
              <TableCell className="figures whitespace-nowrap">
                {formatTime(e.clockInAt)}
                {e.clockInOffsite ? <MapPinOff className="ml-1 inline size-3.5 text-warning" aria-label="Clocked in off site" /> : null}
              </TableCell>
              <TableCell className="figures whitespace-nowrap">
                {e.clockOutAt ? formatTime(e.clockOutAt) : <Badge variant="info">On the clock</Badge>}
                {e.clockOutOffsite ? <MapPinOff className="ml-1 inline size-3.5 text-warning" aria-label="Clocked out off site" /> : null}
              </TableCell>
              <TableCell className="figures text-right">{e.breakMinutes ? `${e.breakMinutes}m` : '—'}</TableCell>
              <TableCell className="figures text-right">
                {e.durationMinutes != null ? hours(e.durationMinutes) : '—'}
                {e.overtimeMinutes ? <span className="block text-xs text-warning">{hours(e.overtimeMinutes)} OT</span> : null}
              </TableCell>
              <TableCell>
                <StatusBadge status={e.status} />
                {e.source === 'manual' ? <span className="ml-1 text-xs text-muted-foreground">manual</span> : null}
              </TableCell>
              <TableCell className="max-w-[16rem] text-xs text-muted-foreground">
                {e.status === 'rejected' && e.rejectionReason ? <span className="block text-destructive">Rejected: {e.rejectionReason}</span> : null}
                <span className="line-clamp-2">{e.notes}</span>
              </TableCell>
              {onEdit ? (
                <TableCell>
                  {e.status !== 'approved' && (manager || e.userId === viewerId) ? (
                    <Button size="icon" variant="ghost" aria-label="Edit entry" onClick={() => onEdit(e)}>
                      <Pencil aria-hidden />
                    </Button>
                  ) : null}
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <RejectDialog open={rejecting} onOpenChange={setRejecting} count={toReject.length} loading={reject.isPending} onConfirm={(reason) => reject.mutate({ ids: toReject.map((e) => e.id), reason })} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekly grid
// ---------------------------------------------------------------------------

export function WeeklyGrid({ sheet, onPickWorker, selectedUserId, showCost }: { sheet: TimesheetData; onPickWorker?: (userId: string) => void; selectedUserId?: string | null; showCost: boolean }) {
  const days = sheet.week.days;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Worker</TableHead>
          {days.map((d) => (
            <TableHead key={d} className="text-right">
              {formatDate(d, undefined, { weekday: 'short' })}
              <span className="block font-normal">{formatDate(d, undefined, { month: 'short', day: 'numeric' })}</span>
            </TableHead>
          ))}
          <TableHead className="text-right">Regular</TableHead>
          <TableHead className="text-right">OT</TableHead>
          <TableHead className="text-right">Total</TableHead>
          {showCost ? <TableHead className="text-right">Labour cost</TableHead> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sheet.rows.map((r) => (
          <TableRow
            key={r.userId}
            data-state={selectedUserId === r.userId ? 'selected' : undefined}
            className={cn(onPickWorker && 'cursor-pointer')}
            onClick={onPickWorker ? () => onPickWorker(r.userId) : undefined}
          >
            <TableCell className="whitespace-nowrap">
              {onPickWorker ? (
                <button type="button" className="font-medium hover:underline" onClick={(e) => (e.stopPropagation(), onPickWorker(r.userId))}>
                  {r.name}
                </button>
              ) : (
                <span className="font-medium">{r.name}</span>
              )}
              <span className="ml-2 inline-flex gap-1 align-middle">
                {r.openShift ? <Badge variant="info">On the clock</Badge> : null}
                {r.statuses.submitted ? <Badge variant="warning">{r.statuses.submitted} to review</Badge> : null}
                {r.statuses.rejected ? <Badge variant="destructive">{r.statuses.rejected} rejected</Badge> : null}
              </span>
            </TableCell>
            {days.map((d) => (
              <TableCell key={d} className={cn('figures text-right', !r.days[d] && 'text-muted-foreground')}>
                {r.days[d] ? hours(r.days[d]) : '—'}
              </TableCell>
            ))}
            <TableCell className="figures text-right">{hours(r.regularMinutes)}</TableCell>
            <TableCell className={cn('figures text-right', r.overtimeMinutes ? 'font-medium text-warning' : 'text-muted-foreground')}>{hours(r.overtimeMinutes)}</TableCell>
            <TableCell className="figures text-right font-medium">{hours(r.totalMinutes)}</TableCell>
            {showCost ? <TableCell className="figures text-right">{formatMoney(r.laborCostCents)}</TableCell> : null}
          </TableRow>
        ))}
        <TableRow className="bg-muted/40 font-medium hover:bg-muted/40">
          <TableCell>Total</TableCell>
          {days.map((d) => (
            <TableCell key={d} className="figures text-right">
              {hours(sheet.rows.reduce((n, r) => n + (r.days[d] ?? 0), 0))}
            </TableCell>
          ))}
          <TableCell className="figures text-right">{hours(sheet.totals.regularMinutes)}</TableCell>
          <TableCell className="figures text-right">{hours(sheet.totals.overtimeMinutes)}</TableCell>
          <TableCell className="figures text-right">{hours(sheet.totals.totalMinutes)}</TableCell>
          {showCost ? <TableCell className="figures text-right">{formatMoney(sheet.totals.laborCostCents)}</TableCell> : null}
        </TableRow>
      </TableBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Labour rates (admins)
// ---------------------------------------------------------------------------

export function LaborRatesDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const members = trpc.projects.timeclock.members.useQuery(undefined, { enabled: open });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Labour rates" description="Hourly cost per worker for job costing. New clock-ins use the current rate; past entries keep theirs." size="lg">
        {members.isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading members…</p>
        ) : members.error ? (
          <div className="space-y-2 py-4 text-center">
            <p className="text-sm text-destructive">{errorMessage(members.error)}</p>
            <Button size="sm" variant="outline" onClick={() => void members.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="max-h-[60vh] divide-y overflow-y-auto">
            {(members.data ?? []).map((m) => (
              <RateRow key={m.userId} member={m} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RateRow({ member }: { member: TimeMember }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const [value, setValue] = useState(centsToDollars(member.laborRateCents));
  const cents = value.trim() === '' ? null : dollarsToCents(value);
  const invalid = value.trim() !== '' && (cents == null || cents < 0);
  const dirty = cents !== member.laborRateCents && !invalid;
  const save = trpc.projects.timeclock.setLaborRate.useMutation({
    onSuccess: async () => {
      toast.success('Labour rate saved', member.name);
      await utils.projects.timeclock.members.invalidate();
    },
    onError: (e) => toast.error('Could not save the rate', errorMessage(e)),
  });
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{member.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {member.email} · {member.role}
        </p>
      </div>
      <div className="relative w-32">
        <Input aria-label={`Hourly rate for ${member.name}`} inputMode="decimal" placeholder="Not costed" value={value} onChange={(e) => setValue(e.target.value)} aria-invalid={invalid || undefined} className="pr-10 text-right figures" />
        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">/h</span>
      </div>
      <Button size="sm" variant="outline" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate({ userId: member.userId, laborRateCents: cents })}>
        Save
      </Button>
    </div>
  );
}
